'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _TagDot;
var _excluded = ["className", "variant", "children"],
  _excluded2 = ["className"],
  _excluded3 = ["className"],
  _excluded4 = ["className"],
  _excluded5 = ["variant", "removeDot", "children"];
import * as React from 'react';
import { cn, getFromNestedObject } from "../utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var TAG_VARIANTS = {
  text: 'tw-bg-primary-light-100 tw-text-primary-light-400 dark:tw-bg-primary-dark-500 dark:tw-text-neutral-light-100',
  state: {
    active: 'tw-bg-success-100 tw-text-success-500',
    pending: 'tw-bg-neutral-light-300 tw-text-neutral-light-700',
    hold: 'tw-bg-on-hold-100 tw-text-on-hold-500',
    warning: 'tw-bg-warning-100 tw-text-warning-600',
    info: 'tw-bg-info-100 tw-text-info-500',
    deleted: 'tw-bg-alert-100 tw-text-alert-500'
  },
  user: {
    advanced: 'tw-text-neutral-light-100 tw-bg-info-400',
    intermediate: 'tw-text-neutral-light-800 tw-bg-success-400',
    beginner: 'tw-text-neutral-light-800 tw-bg-warning-400'
  }
};

// --- Context ---

var TagContext = /*#__PURE__*/React.createContext(undefined);
function useTagContext() {
  var context = React.useContext(TagContext);
  if (!context) {
    throw new Error('Tag compound components must be used within <TagRoot>');
  }
  return context;
}

// --- TagRoot ---

var TagRoot = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'text' : _ref$variant,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/_jsx(TagContext.Provider, {
    value: {
      variant: variant
    },
    children: /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
      ref: ref,
      className: cn('focus:tw-ring-neutral-950 dark:focus:tw-ring-neutral-300 tw-font-caption tw-inline-flex tw-h-2.4 tw-items-center tw-rounded-full tw-px-1.2 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2', getFromNestedObject(TAG_VARIANTS, variant.split('.')), className)
    }, props), {}, {
      children: /*#__PURE__*/_jsx("span", {
        className: "tw-flex tw-items-center tw-justify-center tw-gap-0.8",
        children: children
      })
    }))
  });
});
TagRoot.displayName = 'TagRoot';

// --- TagDot ---

var TagDot = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    props = _objectWithoutProperties(_ref2, _excluded2);
  useTagContext();
  return /*#__PURE__*/_jsx("span", _objectSpread(_objectSpread({
    ref: ref,
    "aria-hidden": "true",
    className: cn('tw-flex tw-h-0.4 tw-w-0.4 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full', className)
  }, props), {}, {
    children: "\u25CF"
  }));
});
TagDot.displayName = 'TagDot';

// --- TagText ---

var TagText = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    props = _objectWithoutProperties(_ref3, _excluded3);
  useTagContext();
  return /*#__PURE__*/_jsx("span", _objectSpread({
    ref: ref,
    className: cn('tw-line-clamp-1', className)
  }, props));
});
TagText.displayName = 'TagText';

// --- TagIcon ---

var TagIcon = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
    props = _objectWithoutProperties(_ref4, _excluded4);
  useTagContext();
  return /*#__PURE__*/_jsx("span", _objectSpread({
    ref: ref,
    className: cn('tw-flex tw-shrink-0 tw-items-center tw-justify-center', className)
  }, props));
});
TagIcon.displayName = 'TagIcon';

// --- Legacy Tag (deprecated) ---

/** @deprecated Use TagRoot, TagDot, TagText, TagIcon instead */

/**
 * @deprecated Use compound components instead:
 * ```tsx
 * <TagRoot variant="state.active">
 *   <TagDot />
 *   <TagText>Published</TagText>
 * </TagRoot>
 * ```
 */
var Tag = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var _ref5$variant = _ref5.variant,
    variant = _ref5$variant === void 0 ? 'text' : _ref5$variant,
    _ref5$removeDot = _ref5.removeDot,
    removeDot = _ref5$removeDot === void 0 ? false : _ref5$removeDot,
    children = _ref5.children,
    props = _objectWithoutProperties(_ref5, _excluded5);
  return /*#__PURE__*/_jsxs(TagRoot, _objectSpread(_objectSpread({
    ref: ref,
    variant: variant
  }, props), {}, {
    children: [variant.includes('state') && !removeDot ? _TagDot || (_TagDot = /*#__PURE__*/_jsx(TagDot, {})) : null, /*#__PURE__*/_jsx(TagText, {
      children: children
    })]
  }));
});
Tag.displayName = 'Tag';
export { Tag, TagRoot, TagDot, TagText, TagIcon, TAG_VARIANTS };